import { useMemo } from 'react';

import type { ProductDetailsFragment } from '@boc/framework/saleor-graphql';
import { cn } from '@boc/ui';

import { useProductDetails } from '~/framework/routes/products/providers';

type ProductVariants = NonNullable<ProductDetailsFragment['variants']>;
type ProductVariant = ProductVariants[number];
type ProductType = ProductDetailsFragment['productType'];

interface AttributeGroup {
  slug: string;
  name: string;
  values: {
    slug: string;
    name: string;
    variantIds: string[];
    isAvailable: boolean;
  }[];
}

/**
 * Extract variant-defining attributes based on productType.assignedVariantAttributes
 * Falls back to using variant names if no selectable attributes are defined
 */
const getVariantDefiningAttributes = (
  variants: ProductVariants,
  productType: ProductType
): AttributeGroup[] => {
  if (variants.length <= 1) {
    return [];
  }

  // Only use attributes where variantSelection is true
  const selectionAttributes =
    productType?.assignedVariantAttributes?.filter((attr) => attr.variantSelection) || [];

  // If no selectable attributes, fall back to using variant names
  if (selectionAttributes.length === 0) {
    return [
      {
        slug: 'variant-name',
        name: 'Variant',
        values: variants.map((variant) => ({
          slug: variant.id,
          name: variant.name || variant.id,
          variantIds: [variant.id],
          isAvailable: variant.quantityAvailable == null || variant.quantityAvailable > 0,
        })),
      },
    ];
  }

  const definingAttributes: AttributeGroup[] = [];

  // For each attribute marked for variant selection
  selectionAttributes.forEach((assignedAttr) => {
    const attrSlug = assignedAttr.attribute.slug;
    if (!attrSlug) return;

    const valuesMap = new Map<string, Set<string>>();

    // Build a map of values to variant IDs for this attribute
    variants.forEach((variant) => {
      const variantAttr = variant.attributes.find((a) => a.attribute.slug === attrSlug);
      if (!variantAttr) return;

      variantAttr.values.forEach((val) => {
        const valueSlug = val.slug || val.name || '';

        if (!valuesMap.has(valueSlug)) {
          valuesMap.set(valueSlug, new Set());
        }

        valuesMap.get(valueSlug)!.add(variant.id);
      });
    });

    // Only include if there are multiple values
    if (valuesMap.size > 1) {
      const values = Array.from(valuesMap.entries()).map(([valueSlug, variantIds]) => {
        const firstVariantWithValue = variants.find((v) =>
          v.attributes.some(
            (a) =>
              a.attribute.slug === attrSlug &&
              a.values.some((val) => (val.slug || val.name) === valueSlug)
          )
        );

        const valueName =
          firstVariantWithValue?.attributes
            .find((a) => a.attribute.slug === attrSlug)
            ?.values.find((v) => (v.slug || v.name) === valueSlug)?.name || valueSlug;

        const isAvailable = Array.from(variantIds).some((id) => {
          const variant = variants.find((v) => v.id === id);

          return variant?.quantityAvailable == null || variant.quantityAvailable > 0;
        });

        return {
          slug: valueSlug,
          name: valueName,
          variantIds: Array.from(variantIds),
          isAvailable,
        };
      });

      definingAttributes.push({
        slug: attrSlug,
        name: assignedAttr.attribute.name || attrSlug,
        values,
      });
    }
  });

  return definingAttributes;
};

/**
 * Get the current attribute selections from the active variant
 */
const getCurrentSelections = (
  variant: ProductVariant,
  definingAttributes: AttributeGroup[]
): Map<string, string> => {
  const selections = new Map<string, string>();

  definingAttributes.forEach((attr) => {
    // Special case: if using variant name selection
    if (attr.slug === 'variant-name') {
      selections.set('variant-name', variant.id);
      return;
    }

    const variantAttr = variant.attributes.find((a) => a.attribute.slug === attr.slug);

    if (variantAttr && variantAttr.values.length > 0) {
      const firstValue = variantAttr.values[0];

      if (firstValue) {
        const valueSlug = firstValue.slug || firstValue.name || '';

        selections.set(attr.slug, valueSlug);
      }
    }
  });

  return selections;
};

/**
 * Find a variant that matches the given attribute selections
 */
const findMatchingVariant = (
  variants: ProductVariants,
  selections: Map<string, string>
): string | null => {
  // Special case: if using variant name selection
  if (selections.has('variant-name')) {
    return selections.get('variant-name') || null;
  }

  const variant = variants.find((v) => {
    // Check if this variant matches all selections
    return Array.from(selections.entries()).every(([attrSlug, valueSlug]) => {
      const variantAttr = v.attributes.find((a) => a.attribute.slug === attrSlug);

      if (!variantAttr) return false;

      return variantAttr.values.some((val) => (val.slug || val.name) === valueSlug);
    });
  });

  return variant?.id || null;
};

export const VariantSelector: React.FC = () => {
  const { product, variant, setVariant } = useProductDetails();

  const variants = product.variants ?? [];

  // Find the full variant data from product.variants using the selected variant id
  const selectedVariant = variants.find((v) => v.id === variant.id);

  const definingAttributes = useMemo(
    () => getVariantDefiningAttributes(variants, product.productType),
    [variants, product.productType]
  );

  const currentSelections = useMemo(
    () => (selectedVariant ? getCurrentSelections(selectedVariant, definingAttributes) : new Map()),
    [selectedVariant, definingAttributes]
  );

  // Check if we should use image thumbnails (only one defining attribute)
  const useThumbnails = definingAttributes.length === 1;

  if (definingAttributes.length === 0) {
    // No variant options to select
    return null;
  }

  const handleAttributeChange = (attributeSlug: string, valueSlug: string) => {
    const newSelections = new Map(currentSelections);

    newSelections.set(attributeSlug, valueSlug);

    const matchingVariantId = findMatchingVariant(variants, newSelections);

    if (matchingVariantId) {
      setVariant(matchingVariantId);
    }
  };

  // Get the first variant for each attribute value (for thumbnail display)
  const getVariantForValue = (variantIds: string[]) => {
    return variants.find((v) => variantIds.includes(v.id));
  };

  const MAX_VALUES_FOR_BUTTONS = 6;

  return (
    <div className="flex flex-col gap-4">
      {definingAttributes.map((attribute) => {
        const useDropdown = attribute.values.length > MAX_VALUES_FOR_BUTTONS;

        return (
          <div key={attribute.slug} className="flex flex-col gap-2">
            <div className="text-primary-2-80 text-sm font-medium">
              {attribute.name}
              {currentSelections.has(attribute.slug) && !useDropdown && (
                <span className="ml-2 font-normal">
                  {
                    attribute.values.find((v) => v.slug === currentSelections.get(attribute.slug))
                      ?.name
                  }
                </span>
              )}
            </div>

            {useDropdown ? (
              // Dropdown selector for many options
              <select
                value={currentSelections.get(attribute.slug) || ''}
                onChange={(e) => handleAttributeChange(attribute.slug, e.target.value)}
                className="border-primary-3-30 text-primary-2-80 hover:border-primary-3-50 focus:border-primary-1 focus:ring-primary-1 rounded-md border bg-white px-4 py-2 text-sm font-medium focus:ring-2 focus:ring-offset-2 focus:outline-none"
              >
                {attribute.values.map((value) => (
                  <option key={value.slug} value={value.slug} disabled={!value.isAvailable}>
                    {value.name}
                  </option>
                ))}
              </select>
            ) : useThumbnails ? (
              // Image thumbnail selector for single attribute
              <div className="flex flex-wrap gap-3">
                {attribute.values.map((value) => {
                  const isSelected = currentSelections.get(attribute.slug) === value.slug;
                  const variantForValue = getVariantForValue(value.variantIds);
                  const imageUrl = variantForValue?.media?.[0]?.url || product.thumbnail?.url;

                  return (
                    <button
                      key={value.slug}
                      type="button"
                      onClick={() => handleAttributeChange(attribute.slug, value.slug)}
                      disabled={!value.isAvailable}
                      className={cn(
                        'relative overflow-hidden rounded-md border-2 transition-all',
                        isSelected
                          ? 'border-primary-1 ring-primary-1 ring-2 ring-offset-2'
                          : 'border-primary-3-30 hover:border-primary-3-50',
                        !value.isAvailable && 'cursor-not-allowed opacity-50'
                      )}
                      title={value.name}
                    >
                      <img src={imageUrl} alt={value.name} className="h-16 w-16 object-cover" />
                      {isSelected && <div className="bg-primary-1/10 absolute inset-0" />}
                    </button>
                  );
                })}
              </div>
            ) : (
              // Text button selector for multiple attributes
              <div className="flex flex-wrap gap-2">
                {attribute.values.map((value) => {
                  const isSelected = currentSelections.get(attribute.slug) === value.slug;

                  return (
                    <button
                      key={value.slug}
                      type="button"
                      onClick={() => handleAttributeChange(attribute.slug, value.slug)}
                      disabled={!value.isAvailable}
                      className={cn(
                        'rounded-md border px-4 py-2 text-sm font-medium transition-colors',
                        isSelected
                          ? 'border-primary-1 bg-primary-1 text-primary-2'
                          : 'border-primary-3-30 text-primary-2-80 hover:border-primary-3-50 bg-white',
                        !value.isAvailable && 'cursor-not-allowed opacity-50'
                      )}
                    >
                      {value.name}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};
